import React, {useRef, useState} from 'react';
import {Outlet, useNavigate} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faMoon, faSun, faSignOutAlt, faChevronDown, faUserPen, faCommentDots, faBookOpen} from '@fortawesome/free-solid-svg-icons';
import {GlobalToaster} from './UI/GlobalToaster';
import {useAuth} from '../contexts/AuthContext';
import {useTheme} from '../contexts/ThemeContext';
import {supportZaloUrl} from '../utils/constants';
import {useClickOutside} from '../hooks/useClickOutside';
import {useTenantNotifications} from '../hooks/useTenantNotifications';
import {NotificationBell} from './Layout/NotificationBell';
import {TenantAvatar} from './UI/TenantAvatar';

/**
 * Khung trang cho NGƯỜI THUÊ.
 *
 * Người thuê chỉ có đúng một trang (Phòng của tôi) nên sidebar điều hướng là
 * thừa: bỏ hẳn, thay bằng một thanh trên cùng gọn với menu tài khoản. Toàn bộ
 * chiều ngang màn hình dành cho nội dung - quan trọng trên điện thoại.
 */
export const TenantLayout: React.FC = () => {
    const navigate = useNavigate();
    const {logout, user} = useAuth();
    const {isDarkMode, toggleDarkMode} = useTheme();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const {notifications, unseenCount, markAllSeen} = useTenantNotifications();

    useClickOutside(menuRef, menuOpen, () => setMenuOpen(false));

    const displayName = user?.displayName || user?.email || '';

    return (
        <div className="min-h-app lg-app-bg flex flex-col">
            {/* `.safe-top`: xem chú thích cùng chỗ ở AppHeader.tsx */}
            <header className="sticky top-0 z-30 glass-toolbar safe-top">
                <div className="max-w-2xl mx-auto h-14 px-3 sm:px-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <img src="/images/logo.svg" alt="" className="w-8 h-8"/>
                        <span className="font-bold text-slate-800 dark:text-white">Nhà 48</span>
                    </div>

                    <div className="flex items-center gap-1">
                        <NotificationBell
                            notifications={notifications}
                            badgeCount={unseenCount}
                            heading="Thông báo"
                            emptyText="Chưa có thông báo nào"
                            onOpen={markAllSeen}
                        />

                        <div className="relative" ref={menuRef}>
                            <button
                                onClick={() => setMenuOpen(o => !o)}
                                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full glass-btn"
                                aria-haspopup="menu"
                                aria-expanded={menuOpen}
                            >
                                <TenantAvatar name={user?.displayName ?? undefined} email={user?.email ?? undefined} size="sm"/>
                                <FontAwesomeIcon icon={faChevronDown} className="text-xs text-slate-500"/>
                            </button>

                            {menuOpen && (
                            <div
                                role="menu"
                                className="absolute right-0 mt-2 w-60 glass-panel p-2 animate-fade-in-down origin-top-right"
                            >
                                <p className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400 truncate border-b border-white/40 dark:border-white/10 mb-1">
                                    {displayName}
                                </p>
                                {/* Người thuê đi tìm "sửa tên / SĐT / email" ở menu
                                    tài khoản trước tiên. Màn hình sửa nằm trong
                                    sheet Chi tiết phòng, nên mở nó bằng query
                                    param thay vì nhân đôi một màn hình nữa. */}
                                <button
                                    onClick={() => {
                                        navigate('/phong-cua-toi?hoso=1');
                                        setMenuOpen(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm
                                        text-slate-700 dark:text-slate-200 hover:bg-white/60 dark:hover:bg-white/10 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faUserPen} className="w-4"/>
                                    Hồ sơ của tôi
                                </button>
                                <button
                                    onClick={() => {
                                        toggleDarkMode();
                                        setMenuOpen(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm
                                        text-slate-700 dark:text-slate-200 hover:bg-white/60 dark:hover:bg-white/10 transition-colors"
                                >
                                    <FontAwesomeIcon icon={isDarkMode ? faSun : faMoon} className="w-4"/>
                                    {isDarkMode ? 'Chế độ sáng' : 'Chế độ tối'}
                                </button>
                                {/* Hỗ trợ kỹ thuật là việc hiếm dùng nên nằm trong
                                    menu, không đáng một nút nổi đè lên nội dung
                                    như ở trang đăng nhập (màn hình đó trống trơn). */}
                                <a
                                    href={supportZaloUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => setMenuOpen(false)}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm
                                        text-slate-700 dark:text-slate-200 hover:bg-white/60 dark:hover:bg-white/10 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faCommentDots} className="w-4"/>
                                    Hỗ trợ kỹ thuật
                                </a>
                                <button
                                    onClick={logout}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm
                                        text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faSignOutAlt} className="w-4"/>
                                    Đăng xuất
                                </button>
                            </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* content-clip-x: xem index.css - chữ cỡ lớn không được kéo lệch trang */}
            <main className="flex-1 w-full content-clip-x">
                <Outlet/>
            </main>

            {/* Hướng dẫn nằm ở chân trang chứ không nằm trong menu tài khoản: người
                thuê mới chưa biết có hướng dẫn thì không ai đi mở menu để tìm. Đặt
                đúng chỗ và đúng kiểu như ở trang đăng nhập, cuộn hết trang là thấy. */}
            <footer className="py-5 safe-bottom text-center text-xs text-slate-500 dark:text-slate-500 space-y-2">
                <button
                    type="button"
                    onClick={() => navigate('/huong-dan')}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium
                        text-indigo-600 dark:text-indigo-400 hover:bg-indigo-500/10 transition-colors"
                >
                    <FontAwesomeIcon icon={faBookOpen}/>
                    Hướng dẫn dùng app cho người thuê
                </button>
                <p>Nhà 48 · Số 48, đường số 6</p>
            </footer>

            <GlobalToaster/>
        </div>
    );
};
